import { get } from '@ebay/presence-ui/lib/object-helpers';
import storeEvent from '@ebay/presence-ui/lib/store-event';
import { hasCurrentCategory } from './util';

const SEL = {
    content: '.str-left-nav__content',
    itemCard: '.str-item-card',
    sectionBody: '.str-left-nav__section-body',
    tabsSectionWrap: '.str-tabs__section-wrap',
    leftNavSectionWrap: '.str-left-nav__section-wrap'
};
const CLS = {
    fixed: 'fixed',
    expanded: 'expanded',
    open: 'open'
};

export default class {
    onCreate(input) {
        const catModule = get(input, 'page.modules.STORE_CATEGORIES_MODULE', null);
        const categories = get(catModule, 'categories', []);
        const hasCurrentCat = hasCurrentCategory(categories);
        this.state = {
            categories,
            isOpen: hasCurrentCat,
            openClass: hasCurrentCat ? CLS.open : '',
            stickyClass: '',
            section: {
                shipTo: initShipToState(input)
            },
            a11yTitle: get(catModule, 'title.text', 'Filter by category')
        };
    }
    onMount() {
        this.handleCategoryTabbing();
        this.root = this.getEl('str-left-nav');
        if (this.root) {
            this.currentTabIndex = 0;
            this.handleToggleLeftNav();
            this.handleStickyHeader();
            this.handleStoreTabChange();
        }
    }
    onRender() {
        this.observeToPad();
    }
    handleStoreTabChange() {
        storeEvent.tabHasChanged.listen(data => {
            this.currentTabIndex = data.tabIndex;
        });
    }
    handleToggleLeftNav() {
        storeEvent.toggleLeftNav.listen(() => {
            // If toggle button clicked while on another tab,
            // change to shopTab first, then open if not already open.
            if (this.currentTabIndex !== 0) {
                storeEvent.changeToTab.dispatch({
                    tabIndex: 0,
                    skipFocus: true
                });
                if (!this.state.isOpen) {
                    this.open();
                }
            } else if (this.state.isOpen) {
                this.close();
            } else {
                this.open();
            }
            doubleRaf(() => {
                storeEvent.toggleLeftNavDone.dispatch({ isOpen: this.state.isOpen });
            });
        });
    }
    open() {
        // Need to render before sliding open
        this.state.isOpen = true;
        doubleRaf(() => {
            this.state.openClass = CLS.open;
        });
    }
    close() {
        // Need to slide close before rendering
        this.state.openClass = '';
        doubleRaf(() => {
            this.state.isOpen = false;
        });
    }
    handleStickyHeader() {
        storeEvent.stickyLeftNav.listen(data => {
            if (data.isSticky) {
                this.state.stickyClass = CLS.fixed;
            } else {
                this.state.stickyClass = '';
            }
        });
    }
    expandSection(sectionName) {
        const section = this.state.section[sectionName];
        if (section) {
            section.isExpanded = !section.isExpanded;
            this.setStateDirty(sectionName);
            doubleRaf(() => {
                section.expandedClass = section.isExpanded ? CLS.expanded : '';
                this.setStateDirty(sectionName);
            });
        }
    }
    // There's an issue when there is a tall leftNav, short main content, with viewport at a
    // height which is taller than main content but shorter than leftNav.
    //
    // As the page is scrolled down, the leftNav will become fixed at a certain point.
    // Fixed elements are taken out of the flow. So if there's not enough content to
    // keep the scroll position, the page scrolls back down, which removes the leftNav position:fixed.
    // This causes a self-inducing loop where the leftNav becomes fixed only to become unfixed
    // because there's not enough content to keep scroll position.
    //
    // The current solution is to pad the section content to roughly match leftNav height.
    // Need to observe the section content for changes in height, as it can change when
    // a different category is selected and new items are rendered.
    //
    // The leftNav height can also change if a category sublist is expanded or collapsed,
    // or when the leftNav itself is hidden or shown. So the leftNav must also be observed.
    observeToPad() {
        if (typeof document !== 'undefined') {
            this.tabsSectionWrap = document.querySelector(SEL.tabsSectionWrap);
            this.leftNavSectionWrap = document.querySelector(SEL.leftNavSectionWrap);
            if (this.tabsSectionWrap && this.leftNavSectionWrap) {
                new ResizeObserver(() => {
                    this.padToMatchLeftNav();
                }).observe(this.tabsSectionWrap);

                new ResizeObserver(() => {
                    this.padToMatchLeftNav();
                }).observe(this.leftNavSectionWrap);
            }
        }
    }
    padToMatchLeftNav() {
        if (this.tabsSectionWrap && this.leftNavSectionWrap) {
            let tabHt = 0;
            const leftNavHt = getHeight(this.leftNavSectionWrap);

            // Need cumulative children height, not sectionWrap height, because
            // sectionWrap is altered by the paddingBottom style we add below.
            if (this.tabsSectionWrap.children) {
                for (let i = 0, n = this.tabsSectionWrap.children.length; i < n; i++) {
                    tabHt += getHeight(this.tabsSectionWrap.children[i]);
                }
            }
            if (leftNavHt > tabHt) {
                this.tabsSectionWrap.style.paddingBottom = `${leftNavHt - tabHt}px`;
            } else {
                this.tabsSectionWrap.style.paddingBottom = '';
            }
        }
    }
    handleCategoryTabbing() {
        document.addEventListener('keydown', (e) => {
            const focusableElements = 'button, a, input, select';
            const leftNav = document.querySelector('#str-left-nav');
            const navFirstFocusableEl = leftNav.querySelectorAll(focusableElements)[0];
            const navfocusableContent = leftNav.querySelectorAll(focusableElements);
            const navLastFocusableEl = navfocusableContent[navfocusableContent.length - 1];
            const categoriesBtn = document.querySelector('.str-controlbar__left-nav-btn button');
            const shopBtn = document.querySelector('#str-tab-tab0');
            const searchBox = document.querySelector('#search-static');
            const shopSection = document.querySelector('.str-tabs__section-wrap');
            const shopFirstFocusableEl = shopSection.querySelectorAll(focusableElements)[0];
            const shopSecondFocusableEl = shopSection.querySelectorAll(focusableElements)[1];

            if (leftNav.classList.contains('open')) {
                const isTabPressed = e.key === 'Tab' || e.keyCode === 9;

                if (!isTabPressed) {
                    return;
                }

                if (e.shiftKey) { // if shift key pressed for shift + tab combination
                    if (document.activeElement === navFirstFocusableEl) {
                        categoriesBtn.focus();
                        e.preventDefault();
                    } else if (document.activeElement === shopBtn) {
                        navLastFocusableEl.focus();
                        e.preventDefault();
                    } else if (document.activeElement === shopFirstFocusableEl) {
                        searchBox.focus();
                        e.preventDefault();
                    } else if (document.activeElement === shopSecondFocusableEl &&
                        shopFirstFocusableEl.ariaDisabled === 'true') {
                        searchBox.focus();
                        e.preventDefault();
                    }
                } else { // if tab key is pressed
                    if (document.activeElement === categoriesBtn) {
                        navFirstFocusableEl.focus();
                        e.preventDefault();
                    } else if (document.activeElement === navLastFocusableEl) {
                        shopBtn.focus();
                        e.preventDefault();
                    } else if (document.activeElement === searchBox) {
                        if (shopFirstFocusableEl.ariaDisabled === 'true') { // in case carousel button is disabled
                            shopSecondFocusableEl.focus();
                            e.preventDefault();
                        } else {
                            shopFirstFocusableEl.focus();
                            e.preventDefault();
                        }
                    }
                }
            }
        });
    }
}

function initShipToState(input) {
    const shipTo = get(input, 'page.modules.SHIPPING_DESTINATION_MODULE.shippingToLocation', null);
    const currentZip = get(shipTo, 'minView.zipCode.label.textSpans.0.text', '');
    const isExpanded = !currentZip; // If no currentZip, then expand section body
    return {
        isExpanded: isExpanded,
        expandedClass: isExpanded ? CLS.expanded : ''
    };
}

function doubleRaf(callback) {
    requestAnimationFrame(() => requestAnimationFrame(() => {
        callback();
    }));
}

function getHeight(el) {
    const styles = window.getComputedStyle(el);
    const margin = parseFloat(styles.marginTop) + parseFloat(styles.marginBottom);
    return Math.ceil(el.offsetHeight + margin);
}
